import type { JSX } from 'react';
import { useState } from 'react';
import { Alert, Button, Center, Group, Loader, Modal, Paper, Stack, Text, Textarea } from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, commentApi, type TaskComment } from '../../api';
import { formatDateTime } from '../../format';

const MAX = 4000;

/**
 * Comments on one task (AP-108): the team's notes in order of time. Anyone
 * who sees the schedule writes; one's own comment can be changed, removed by
 * oneself or by project management.
 */
export function TaskCommentsModal({ projectId, task, onClose }: {
  projectId: string;
  task: { id: string; code: string; name: string } | null;
  onClose: () => void;
}): JSX.Element {
  return (
    <Modal opened={task !== null} onClose={onClose} size="lg" title={task ? `Kommentare · ${task.code} ${task.name}` : ''}>
      {task && <CommentsBody projectId={projectId} taskId={task.id} />}
    </Modal>
  );
}

function CommentsBody({ projectId, taskId }: { projectId: string; taskId: string }): JSX.Element {
  const queryClient = useQueryClient();
  const key = ['task-comments', projectId, taskId];
  const list = useQuery({ queryKey: key, queryFn: () => commentApi.list(projectId, taskId) });
  const [draft, setDraft] = useState('');
  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: key });
    await queryClient.invalidateQueries({ queryKey: ['task-comment-counts', projectId] });
  };
  const add = useMutation({
    mutationFn: () => commentApi.create(projectId, taskId, draft.trim()),
    onSuccess: () => setDraft(''),
    onSettled: refresh,
  });
  const error = (e: unknown): string => (e instanceof ApiError ? e.message : 'Speichern fehlgeschlagen.');

  return (
    <Stack gap="sm">
      {list.isPending && <Center h={60}><Loader size="sm" /></Center>}
      {list.isError && <Alert color="red">{error(list.error)}</Alert>}
      {list.data && list.data.length === 0 && <Text size="sm" c="dimmed">Noch keine Kommentare.</Text>}
      {list.data?.map((c) => <CommentItem key={c.id} projectId={projectId} comment={c} onChanged={refresh} />)}
      <Textarea
        label="Neuer Kommentar"
        autosize
        minRows={2}
        maxRows={10}
        maxLength={MAX}
        value={draft}
        onChange={(e) => setDraft(e.currentTarget.value)}
        onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && draft.trim()) add.mutate(); }}
        description="Strg+Enter schickt ab. Sichtbar für alle, die den Terminplan sehen."
      />
      {add.isError && <Alert color="red" py={6}>{error(add.error)}</Alert>}
      <Group justify="flex-end">
        <Button size="xs" onClick={() => add.mutate()} loading={add.isPending} disabled={!draft.trim()}>Kommentieren</Button>
      </Group>
    </Stack>
  );
}

function CommentItem({ projectId, comment: c, onChanged }: { projectId: string; comment: TaskComment; onChanged: () => Promise<void> }): JSX.Element {
  const [editing, setEditing] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: (body: string) => commentApi.update(projectId, c.id, body),
    onSuccess: () => setEditing(null),
    onSettled: onChanged,
  });
  const remove = useMutation({ mutationFn: () => commentApi.remove(projectId, c.id), onSettled: onChanged });
  const failed = save.error ?? remove.error;

  return (
    <Paper withBorder p="xs" radius="sm">
      <Group justify="space-between" gap="xs" wrap="nowrap">
        <Text size="xs" fw={600}>{c.authorName}</Text>
        <Text size="xs" c="dimmed">{formatDateTime(c.createdAt)}{c.editedAt ? ` · geändert ${formatDateTime(c.editedAt)}` : ''}</Text>
      </Group>
      {editing === null ? (
        <Text size="sm" mt={4} style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{c.body}</Text>
      ) : (
        <Stack gap={4} mt={4}>
          <Textarea autosize minRows={2} maxRows={10} maxLength={MAX} value={editing} onChange={(e) => setEditing(e.currentTarget.value)} />
          <Group gap="xs" justify="flex-end">
            <Button size="compact-xs" variant="default" onClick={() => setEditing(null)}>Abbrechen</Button>
            <Button size="compact-xs" onClick={() => save.mutate(editing.trim())} loading={save.isPending} disabled={!editing.trim()}>Speichern</Button>
          </Group>
        </Stack>
      )}
      {editing === null && (c.mine || c.canDelete) && (
        <Group gap="xs" justify="flex-end" mt={4}>
          {c.mine && <Button size="compact-xs" variant="subtle" onClick={() => setEditing(c.body)}>Ändern</Button>}
          {c.canDelete && (
            <Button size="compact-xs" variant="subtle" color="red" loading={remove.isPending}
              onClick={() => { if (window.confirm('Diesen Kommentar löschen? Zurück nur über einen Snapshot des Projekts.')) remove.mutate(); }}>
              Löschen
            </Button>
          )}
        </Group>
      )}
      {failed && <Alert color="red" py={4} mt={4}>{failed instanceof ApiError ? failed.message : 'Fehlgeschlagen.'}</Alert>}
    </Paper>
  );
}
